import $ from 'jquery';
import fetch from 'fetch';
import config from 'sh-www/config/environment';
import Service, { inject as service } from '@ember/service';
import { normalizeErrors } from 'sh-www/utils/adapter-errors';
import { dropTask } from 'ember-concurrency';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { blockingLocationAssign } from 'sh-www/utils/window';
import { STORAGE_KEYS } from 'sh-www/services/local-storage-fallback';

const CONTENT_TYPE = 'Content-Type';
const CONTENT_TYPE_APPLICATION_JSON = 'application/json;charset=UTF-8';
const CONTENT_TYPE_FORM_URLENCODED = 'application/x-www-form-urlencoded;charset=UTF-8';

class ShApiError extends Error {
  constructor(status, payload, errors) {
    super();
    this.status = status;
    this.payload = payload;
    this.errors = errors;
  }
}

const DONT_REDIRECT_MAINTENANCE_ROUTES = new Set([
  '/maintenance',
]);

class ShApiService extends Service {
  @service auth;
  @service cookies;
  @service session;
  @service('local-storage-fallback') storage;

  get(url, options = {}) {
    return this.makeRequest(url, 'GET', options);
  }

  put(url, options = {}) {
    return this.makeRequest(url, 'PUT', options);
  }

  post(url, options = {}) {
    return this.makeRequest(url, 'POST', options);
  }

  patch(url, options = {}) {
    return this.makeRequest(url, 'PATCH', options);
  }

  delete(url, options = {}) {
    return this.makeRequest(url, 'DELETE', options);
  }

  del(url, options = {}) {
    return this.makeRequest(url, 'DELETE', options);
  }

  request(url, options = {}) {
    return this.makeRequest(url, options.method, options);
  }

  async makeRequest(url, method = 'GET', options = {}) {
    const init = await this.normalizeOptions(method, options);
    const resource = this.buildUrl(url, method, options);
    const response = await fetch(resource, init);

    // Maintenance mode
    if (response.status === 503 && !DONT_REDIRECT_MAINTENANCE_ROUTES.has(window.location.pathname)) {
      blockingLocationAssign('/maintenance');
    }

    // If we're unauthenticated, invalidate
    if (response.status === 401) {
      this.session.invalidate();
    }

    // Laravel missing session exception
    if (response.status === 419) {
      this.cookies.clear('XSRF-TOKEN');
    }

    const body = await this.getResponseBody(response);

    // Immediately reload if we're on an unsupported client
    if (
      body?.meta?.web_version
      && body.meta.web_version > config.WEB_APP_CURRENT_VERSION_NUMERIC
    ) {
      const lastWebVersion = this.storage.getItem('last_loaded_web_version');
      if (lastWebVersion != config.WEB_APP_CURRENT_VERSION_NUMERIC) {
        if (config.DEBUG) {
          console.log(`API - Web Client out of date, reloading`);
        }
        
        this.storage.setItem('last_loaded_web_version', config.WEB_APP_CURRENT_VERSION_NUMERIC);
        window.location.reload(true);
      } else {
        /**
         * This is not ideal. Ideally we'd pop a banner or something in this case, or redirect to a page
         * alerting the user that they need to try to reload.  But this should be an exceptional case
         * where our deploy is completely broken.
         */
        if (config.DEBUG) {
          console.log(`API - Web Client out of date, pausing`);
        }
      }

      return new Promise(() => { });
    } else {
      this.storage.removeItem('last_loaded_web_version');
    }

    if (response.ok && (body.data?.type !== 'Error')) {
      return body;
    }

    const status = body.data?.attributes?.['status-code'] || response.status;
    const errors = normalizeErrors(body);

    throw new ShApiError(status, body, errors);
  }

  getResponseBody(response) {
    return response.headers.get('content-type')?.includes('application/json')
      ? response.json()
      : response.text();
  }

  buildUrl(path, method, { params } = { }) {
    const url = config.apiUrl + (path.startsWith('/') ? path : `/${path}`);

    if (params) {
      return `${url}?${$.param(params)}`;
    }

    return url;
  };

  async normalizeOptions(method, { headers, jsonData, data }) {
    const options = {
      method,
      credentials: 'include',
      cache: 'no-cache',
      headers: { ...headers },
    };

    const sessionHeaders = await this.session.sessionHeaders(method);
    options.headers = { ...options.headers, ...sessionHeaders };

    if (this.session.isMasquerading) {
      options.headers.Masquerade = '1';
    }

    if ((method === 'GET') || (method === 'HEAD')) {
      return options;
    }

    if (jsonData) {
      options.headers[CONTENT_TYPE] = CONTENT_TYPE_APPLICATION_JSON;
      options.body = JSON.stringify(jsonData);
    } else if (data) {
      if (FormData.prototype.isPrototypeOf(data)) {
        options.body = data;
      } else if (URLSearchParams.prototype.isPrototypeOf(data)) {
        // Older versions of Safari don't support this and the ember-fetch polyfill doesn't handle it correctly
        // so handle it here
        options.headers[CONTENT_TYPE] = CONTENT_TYPE_FORM_URLENCODED;
        options.body = String(data);
      } else if (typeof data === 'string') {
        options.headers[CONTENT_TYPE] = CONTENT_TYPE_FORM_URLENCODED;
        options.body = data;
      } else {
        throw new Error('Unhandled content-type in api service');
      }
    }

    return options;
  }

  async fetchXsrf() {
    return this.fetchXsrfTask.isRunning
      ? this.fetchXsrfTask.last
      : this.fetchXsrfTask.perform();
  }

  @dropTask
  *fetchXsrfTask() {
    yield this.get(`auth/csrf-cookie`);
  }
}

export default ShApiService;
